import { Box, SxProps, Theme } from '@mui/material';
import MarkdownIt from 'markdown-it';
import markdownItIns from 'markdown-it-ins';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { createSafeHTMLString, EmbedHTML } from '../utils';

const md = new MarkdownIt({ breaks: true, typographer: true })
    .enable('list')
    .enable('heading')
    .disable('image')
    .disable('table')
    .use(markdownItIns);

export function SimpleMarkdownRenderer({ markdown, sx }: { markdown: string; sx?: SxProps<Theme> }) {
    const rawHtml = useMemo(() => {
        const output = md.render(markdown) as string;
        return DOMPurify.sanitize(output);
    }, [markdown]);

    // This is safe because it has already been sanitized by markdown-it and DOMPurify.
    // eslint-disable-next-line rulesdir/no-create-safe-html-without-reason
    const safeHtmlString = createSafeHTMLString(rawHtml);

    return (
        <Box
            data-testid="customer-message-markdown"
            sx={[
                {
                    typography: 'body1',
                    lineHeight: 1.7,
                    '& > *:first-of-type': {
                        mt: 0,
                    },
                    '& > *:last-child': {
                        mb: 0,
                    },
                    '& p': {
                        my: 0,
                        mb: 2,
                    },
                    '& ul, & ol': {
                        mt: 0,
                        mb: 2,
                        pl: 3,
                        listStylePosition: 'inside',
                    },
                    '& a': {
                        wordBreak: 'break-word',
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <EmbedHTML component={Box} safeHTMLString={safeHtmlString} />
        </Box>
    );
}

